import { calculateAudioMOS, calculateVideoMOS } from 'utils/webrtc/stats/mosScores';
import {
  computeAudioImpairmentPct,
  concealmentPenalty,
} from 'utils/webrtc/stats/concealment-penalty';
import {
  InboundAudioFields,
  InboundVideoFields,
  LimitingFactor,
  OutboundAudioFields,
  OutboundVideoFields,
  ScoringResult,
} from 'utils/webrtc/stats/types';

const MIN_MOS = 1;
const MAX_MOS = 5;

// Heuristic thresholds for attributing the dominant degradation; starting values,
// tune from production traces.
const LOSS_THRESHOLD = 0.02; // 2% fraction loss
const RTT_THRESHOLD_MS = 200;
const AUDIO_JITTER_THRESHOLD_MS = 30;
const VIDEO_JITTER_THRESHOLD_MS = 100;
export const LIMITING_FACTOR_SCORE_CEILING = 4.0;

const isFiniteNumber = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

export const clampMOS = (value: number): number => Math.min(Math.max(value, MIN_MOS), MAX_MOS);

// Bitrate is required for the bPPPF model — without a positive rate the score collapses
// to the floor. False on a track's first observation (no delta yet) and on silent ticks
// (zero bytes); both surface as "no score" upstream.
const hasMeasurableBitrate = (bitrate: number | undefined): bitrate is number =>
  isFiniteNumber(bitrate) && bitrate > 0;

// Precedence loss → rtt → jitter, by perceptual weight. No 'bitrate' member by
// design: it would mislabel a clean-network bPPPF floor as a network cause.
export const pickLimitingFactor = (
  score: number,
  inputs: { lossFraction: number; rttMs: number; jitterMs: number; isVideo: boolean }
): LimitingFactor | undefined => {
  if (score >= LIMITING_FACTOR_SCORE_CEILING) {
    return undefined;
  }
  if (inputs.lossFraction > LOSS_THRESHOLD) {
    return 'loss';
  }
  if (inputs.rttMs > RTT_THRESHOLD_MS) {
    return 'rtt';
  }
  const jitterThreshold = inputs.isVideo ? VIDEO_JITTER_THRESHOLD_MS : AUDIO_JITTER_THRESHOLD_MS;
  if (inputs.jitterMs > jitterThreshold) {
    return 'jitter';
  }
  return undefined;
};

export function scoreAudioInbound(fields: InboundAudioFields): ScoringResult {
  // Computed even on null-score paths so the dashboard still gets concealment
  // data when packet flow stopped this tick.
  const audioImpairmentPct = computeAudioImpairmentPct(fields.concealment);

  if (!isFiniteNumber(fields.packetsReceived) || fields.packetsReceived <= 0) {
    return { score: null, reason: 'no-packets', audioImpairmentPct };
  }
  if (!isFiniteNumber(fields.jitter)) {
    return { score: null, reason: 'no-jitter', audioImpairmentPct };
  }

  const totalPackets = fields.packetsReceived + Math.max(0, fields.packetsLost);
  const lossFraction = totalPackets > 0 ? fields.packetsLost / totalPackets : 0;

  const baseMOS = calculateAudioMOS({
    packetLoss: lossFraction,
    roundTripTime: fields.roundTripTime,
    fecEnabled: (fields.fecPacketsReceived ?? 0) > 0,
    bufferDelayInMs: fields.averageJitterBufferDelayMs,
  });

  const penalty = concealmentPenalty(fields.concealment);
  const score = clampMOS(baseMOS - penalty);
  const limitingFactor = pickLimitingFactor(score, {
    lossFraction,
    rttMs: (fields.roundTripTime ?? 0) * 1000,
    jitterMs: fields.jitter * 1000,
    isVideo: false,
  });
  return { score, limitingFactor, audioImpairmentPct };
}

export function scoreVideoInbound(fields: InboundVideoFields): ScoringResult {
  if (!isFiniteNumber(fields.packetsReceived) || fields.packetsReceived <= 0) {
    return { score: null, reason: 'no-packets' };
  }
  if (!isFiniteNumber(fields.jitter)) {
    return { score: null, reason: 'no-jitter' };
  }

  const hasResolution = isFiniteNumber(fields.frameWidth) && isFiniteNumber(fields.frameHeight);
  const hasFps = isFiniteNumber(fields.framesPerSecond);
  if (!hasResolution && !hasFps) {
    return { score: null, reason: 'no-resolution' };
  }

  if (!hasMeasurableBitrate(fields.bitrate)) {
    return { score: null, reason: 'no-bitrate' };
  }

  // Remote camera-off: residual packet tail clears the guards above at fps=0;
  // calculateVideoMOS floors to 1.0 as fps→0, so reject instead.
  if (!isFiniteNumber(fields.framesPerSecond) || fields.framesPerSecond < 1) {
    return { score: null, reason: 'no-frames' };
  }

  const baseMOS = calculateVideoMOS({
    frameWidth: fields.frameWidth,
    frameHeight: fields.frameHeight,
    frameRate: fields.framesPerSecond,
    codec: fields.codec,
    bitrate: fields.bitrate,
    roundTripTime: fields.roundTripTime,
    bufferDelayInMs: fields.averageJitterBufferDelayMs,
  });

  const totalPackets = fields.packetsReceived + Math.max(0, fields.packetsLost);
  const lossFraction = totalPackets > 0 ? fields.packetsLost / totalPackets : 0;

  const score = clampMOS(baseMOS);
  const limitingFactor = pickLimitingFactor(score, {
    lossFraction,
    rttMs: (fields.roundTripTime ?? 0) * 1000,
    jitterMs: fields.jitter * 1000,
    isVideo: true,
  });
  return { score, limitingFactor };
}

// Received screenshare is scored on decode health, not bits-per-pixel —
// static content compresses to ~nothing, so bPPPF floors a perfectly healthy
// share. Constants are replay-tuned starting values.
const MIN_FRAME_SAMPLE_PER_TICK = 4;
const DROPPED_FRACTION_WEIGHT = 10;
const FREEZE_EVENT_WEIGHT = 2;
const FREEZE_EVENT_FLOOR = 0.5;
const FREEZE_DURATION_WEIGHT = 1.5;
const FREEZE_PENALTY_CAP = 3;

// Duration-scaled freeze penalty (dump-measured Pareto knee): a short blip on
// an otherwise clean tick stays ≥4, a sustained freeze degrades
// proportionally. Event-count fallback for browsers that report freezeCount
// without totalFreezesDuration.
const freezePenalty = (fields: InboundVideoFields): number => {
  const freezeEvents = fields.freezeCount ?? 0;
  if (freezeEvents <= 0) {
    return 0;
  }
  if (isFiniteNumber(fields.totalFreezesDuration)) {
    return Math.min(
      FREEZE_EVENT_FLOOR + FREEZE_DURATION_WEIGHT * fields.totalFreezesDuration,
      FREEZE_PENALTY_CAP
    );
  }
  return FREEZE_EVENT_WEIGHT * freezeEvents;
};

export function scoreFrameHealthVideoInbound(fields: InboundVideoFields): ScoringResult {
  // Order matters: transport-frozen tracks (packets stop) must null-drop as
  // 'no-packets' before the fps guard can fire.
  if (!isFiniteNumber(fields.packetsReceived) || fields.packetsReceived <= 0) {
    return { score: null, reason: 'no-packets' };
  }
  if (!isFiniteNumber(fields.framesPerSecond) || fields.framesPerSecond < 1) {
    return { score: null, reason: 'no-frames' };
  }

  const framesDropped = fields.framesDropped ?? 0;
  const framesDecoded = fields.framesDecoded ?? 0;
  const frameSample = framesDropped + framesDecoded;
  if (frameSample < MIN_FRAME_SAMPLE_PER_TICK) {
    return { score: null, reason: 'few-frames' };
  }

  const droppedFraction = framesDropped / frameSample;
  const score = clampMOS(
    MAX_MOS - DROPPED_FRACTION_WEIGHT * droppedFraction - freezePenalty(fields)
  );

  const totalPackets = fields.packetsReceived + Math.max(0, fields.packetsLost);
  const lossFraction = totalPackets > 0 ? fields.packetsLost / totalPackets : 0;
  const limitingFactor = pickLimitingFactor(score, {
    lossFraction,
    rttMs: (fields.roundTripTime ?? 0) * 1000,
    jitterMs: fields.jitter * 1000,
    isVideo: true,
  });
  return { score, limitingFactor };
}

export function scoreAudioOutbound(fields: OutboundAudioFields): ScoringResult {
  if (!isFiniteNumber(fields.packetsSent) || fields.packetsSent <= 0) {
    return { score: null, reason: 'no-packets' };
  }
  if (!isFiniteNumber(fields.jitter)) {
    return { score: null, reason: 'no-jitter' };
  }

  const lossFraction =
    fields.packetsSent > 0 ? Math.max(0, fields.packetsLost) / fields.packetsSent : 0;

  const baseMOS = calculateAudioMOS({
    packetLoss: lossFraction,
    roundTripTime: fields.roundTripTime,
  });

  const score = clampMOS(baseMOS);
  const limitingFactor = pickLimitingFactor(score, {
    lossFraction,
    rttMs: (fields.roundTripTime ?? 0) * 1000,
    jitterMs: fields.jitter * 1000,
    isVideo: false,
  });
  return { score, limitingFactor };
}

export function scoreVideoOutbound(fields: OutboundVideoFields): ScoringResult {
  if (!isFiniteNumber(fields.packetsSent) || fields.packetsSent <= 0) {
    return { score: null, reason: 'no-packets' };
  }
  if (!isFiniteNumber(fields.jitter)) {
    return { score: null, reason: 'no-jitter' };
  }

  if (!hasMeasurableBitrate(fields.bitrate)) {
    return { score: null, reason: 'no-bitrate' };
  }

  // Camera-off leaves a residual packet tail that clears the guards above at
  // fps=0; calculateVideoMOS floors to 1.0 as fps→0, so reject instead.
  if (!isFiniteNumber(fields.framesPerSecond) || fields.framesPerSecond < 1) {
    return { score: null, reason: 'no-frames' };
  }

  const baseMOS = calculateVideoMOS({
    frameWidth: fields.frameWidth,
    frameHeight: fields.frameHeight,
    frameRate: fields.framesPerSecond,
    codec: fields.codec,
    bitrate: fields.bitrate,
  });

  const lossFraction =
    fields.packetsSent > 0 ? Math.max(0, fields.packetsLost) / fields.packetsSent : 0;
  const score = clampMOS(baseMOS);
  const limitingFactor = pickLimitingFactor(score, {
    lossFraction,
    rttMs: (fields.roundTripTime ?? 0) * 1000,
    jitterMs: fields.jitter * 1000,
    isVideo: true,
  });
  return { score, limitingFactor };
}

const CPU_LIMITATION_PENALTY = 2;
const TARGET_BITRATE_SHORTFALL_TRIGGER_BPS = 20_000;
const TARGET_BITRATE_SHORTFALL_TRIGGER_FRACTION = 0.05;
const TARGET_BITRATE_SEVERE_DEVIATION = 0.15;
const SEVERE_DEVIATION_PENALTY = 2;
const MODERATE_DEVIATION_PENALTY = 1;

const cpuLimitationPenalty = (fields: OutboundVideoFields): number =>
  fields.qualityLimitationReason === 'cpu' ? CPU_LIMITATION_PENALTY : 0;

// Fires only when the shortfall clears max(20 kbps, 5% of target); −1 for a
// moderate deviation, −2 from 15% up. No-op when the browser reports no
// targetBitrate (Firefox).
const targetBitrateDeviationPenalty = (fields: OutboundVideoFields): number => {
  if (
    !isFiniteNumber(fields.targetBitrate) ||
    fields.targetBitrate <= 0 ||
    !hasMeasurableBitrate(fields.bitrate)
  ) {
    return 0;
  }
  const shortfall = fields.targetBitrate - fields.bitrate;
  const trigger = Math.max(
    TARGET_BITRATE_SHORTFALL_TRIGGER_BPS,
    TARGET_BITRATE_SHORTFALL_TRIGGER_FRACTION * fields.targetBitrate
  );
  if (shortfall <= trigger) {
    return 0;
  }
  return shortfall / fields.targetBitrate >= TARGET_BITRATE_SEVERE_DEVIATION
    ? SEVERE_DEVIATION_PENALTY
    : MODERATE_DEVIATION_PENALTY;
};

// SS-send has no outbound frame-health stats (framesDropped/freezeCount are
// inbound-only) and bPPPF false-poors static shares, so the base is a healthy
// MAX_MOS minus encode-intent penalties; the PC-stability factor still
// multiplies on top.
export function scoreScreenshareVideoOutbound(fields: OutboundVideoFields): ScoringResult {
  if (!isFiniteNumber(fields.packetsSent) || fields.packetsSent <= 0) {
    return { score: null, reason: 'no-packets' };
  }
  // Static shares encode ~no frames; null out instead of emitting a healthy
  // score for a track nobody can assess.
  if (!isFiniteNumber(fields.framesPerSecond) || fields.framesPerSecond < 1) {
    return { score: null, reason: 'no-frames' };
  }
  // No bitrate-based penalty: static screen content compresses to ~nothing,
  // so bitrate behavior must never judge screen content (upstream carve-out).
  return { score: clampMOS(MAX_MOS - cpuLimitationPenalty(fields)) };
}

// The sender cannot observe frame delivery, so score encode intent — CPU
// limitation and target-bitrate shortfall — instead of bPPPF's
// content-compression proxy.
export function scoreCameraVideoOutbound(fields: OutboundVideoFields): ScoringResult {
  if (!isFiniteNumber(fields.packetsSent) || fields.packetsSent <= 0) {
    return { score: null, reason: 'no-packets' };
  }
  // Camera-off leaves a residual packet tail at fps=0; reject instead of
  // scoring a track that renders nothing.
  if (!isFiniteNumber(fields.framesPerSecond) || fields.framesPerSecond < 1) {
    return { score: null, reason: 'no-frames' };
  }

  const score = clampMOS(
    MAX_MOS - cpuLimitationPenalty(fields) - targetBitrateDeviationPenalty(fields)
  );
  const lossFraction =
    fields.packetsSent > 0 ? Math.max(0, fields.packetsLost) / fields.packetsSent : 0;
  const limitingFactor = pickLimitingFactor(score, {
    lossFraction,
    rttMs: (fields.roundTripTime ?? 0) * 1000,
    jitterMs: fields.jitter * 1000,
    isVideo: true,
  });
  return { score, limitingFactor };
}
